var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import { Row } from 'react-bootstrap';
import { LayoutBreadcrumbs } from './LayoutBreadCrumbs';
import { LoadingSpinner } from '../../LoadingSpinners';
/**
 * `LayoutBase` is a generic React component designed to provide a structured layout for pages or sections within an application.
 * It incorporates React Bootstrap components for styling and layout purposes and supports loading states and breadcrumb navigation.
 *
 * @template TData - The type of data expected for the form component within the layout. It should extend from FieldValues of 'react-hook-form'.
 * @param {LayoutBaseProps<TData>} props - The properties passed to the LayoutBase component. These include:
 * @param {React.ReactNode} header - The header content for the layout. It could be a title, navigation, or any custom React element.
 * @param {React.ReactNode} content - The main content of the layout. This is typically the core part of the page or section.
 * @param {React.ReactNode} breadcrumbs - Breadcrumb links for navigation purposes. It uses the `LayoutBreadcrumbs` component.
 * @param {boolean} [isLoading=false] - Optional flag to indicate if the layout is in a loading state. It defaults to false.
 */
export const LayoutBase = (_a) => {
    var { header, content, breadcrumbs, isLoading = false } = _a, rest = __rest(_a, ["header", "content", "breadcrumbs", "isLoading"]);
    const isFormProps = useCallback((props) => {
        return ('control' in props &&
            'getValues' in props &&
            'setValue' in props &&
            'watch' in props &&
            'errors' in props);
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rest]);
    const renderComponent = useCallback((component) => typeof component === 'function' && isFormProps(rest)
        ? component({
            control: rest.control,
            getValues: rest.getValues,
            setValue: rest.setValue,
            watch: rest.watch,
            errors: rest.errors
        })
        : component, [rest, isFormProps]);
    const renderedHeader = useMemo(() => renderComponent(header), [header, renderComponent]);
    const renderedContent = useMemo(() => isLoading ? _jsx(LoadingSpinner, {}) : renderComponent(content), [content, isFormProps, isLoading]);
    const renderBody = useMemo(() => (_jsxs(_Fragment, { children: [header && _jsx(Row, { children: renderedHeader }), _jsx(LayoutBreadcrumbs, { breadcrumbs: breadcrumbs }), content && _jsx(Row, { children: renderedContent })] })), [breadcrumbs, content, header, renderedContent, renderedHeader]);
    return _jsx("div", Object.assign({ className: 'page-content' }, { children: renderBody }));
};
